import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  MoreHorizontal, 
  Plus, 
  Check, 
  AlertCircle,
  X,
  Copy,
  ChevronRight
} from 'lucide-react';
import { CatalogItem, CatalogItemUpdate, PriceHistoryResponse } from '../types/billing';
import { 
  getCatalogItems, 
  updateCatalogItem, 
  getPriceHistory, 
  addNewPrice,
  createCatalogItem 
} from '../utils/billingApi';
import { useAuth } from '../contexts/AuthContext';

const CatalogItemEditPage: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [item, setItem] = useState<CatalogItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<CatalogItemUpdate>({});
  
  // Price history state
  const [priceHistory, setPriceHistory] = useState<PriceHistoryResponse | null>(null);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [showAddPrice, setShowAddPrice] = useState(false);
  const [newPriceValue, setNewPriceValue] = useState<string>('');
  const [addingPrice, setAddingPrice] = useState(false);
  
  // Menu state
  const [showPriceMenu, setShowPriceMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Standard item reference (if this custom item overrides a standard one)
  const [standardItem, setStandardItem] = useState<CatalogItem | null>(null);

  // Check if item is linked to an external catalog
  const isLinkedToExternal = !!(item && (item.linked_immunization_id || item.linked_diagnostic_id || item.linked_medication_id));
  const linkedCatalogType = item?.linked_immunization_id ? 'immunization' : 
                           item?.linked_diagnostic_id ? 'diagnostic' : 
                           item?.linked_medication_id ? 'medication' : null;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowPriceMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load the catalog item
  useEffect(() => {
    const loadItem = async () => {
      if (!user?.practice_id || !itemId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch with include_all_standards=true to get standard items even if overridden
        const items = await getCatalogItems(user.practice_id, undefined, undefined, false, true);
        const foundItem = items.find(i => i.id === itemId);
        
        if (!foundItem) {
          setError('Catalog item not found');
          return;
        }
        
        setItem(foundItem);
        setFormData({
          code: foundItem.code || undefined,
          name: foundItem.name,
          description: foundItem.description || undefined,
          unit_price: foundItem.unit_price,
          unit_cost: foundItem.unit_cost,
          measure_unit: foundItem.measure_unit || undefined,
          taxable: foundItem.taxable,
          allow_price_override: foundItem.allow_price_override,
          display_order: foundItem.display_order
        });
        
        // For custom items, find the matching standard item (if any)
        // A standard item has is_standard=true and matches by name + category
        if (!foundItem.is_standard) {
          const matchingStandard = items.find(i => 
            i.is_standard && 
            i.name === foundItem.name && 
            i.category === foundItem.category
          );
          if (matchingStandard) {
            setStandardItem(matchingStandard);
          }
        }
        
        // Load price history
        setLoadingPrices(true);
        try {
          const history = await getPriceHistory(user.practice_id, itemId);
          setPriceHistory(history);
        } catch (err) {
          console.error('Failed to load price history:', err);
        } finally {
          setLoadingPrices(false);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load catalog item');
      } finally {
        setLoading(false);
      }
    };
    
    loadItem();
  }, [user?.practice_id, itemId]);

  const handleSave = async () => {
    if (!user?.practice_id || !itemId) return;
    
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const updated = await updateCatalogItem(user.practice_id, itemId, formData);
      setItem(updated);
      setSuccessMessage('Changes saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNewPrice = async () => {
    if (!user?.practice_id || !itemId || !newPriceValue || !item) return;
    
    const priceNum = parseFloat(newPriceValue);
    if (isNaN(priceNum) || priceNum <= 0) {
      setError('Please enter a valid price greater than 0');
      return;
    }

    setAddingPrice(true);
    setError(null);
    
    try {
      // For standard items, create a practice override first
      if (item.is_standard) {
        const newItem = await createCatalogItem(user.practice_id, {
          code: item.code || undefined,
          name: item.name,
          category: item.category as any,
          description: item.description || undefined,
          unit_price: priceNum,
          unit_cost: item.unit_cost,
          measure_unit: item.measure_unit || undefined,
          taxable: item.taxable,
          allow_price_override: item.allow_price_override,
          linked_immunization_id: item.linked_immunization_id || undefined,
          linked_diagnostic_id: item.linked_diagnostic_id || undefined,
          linked_medication_id: item.linked_medication_id || undefined,
          linked_procedure_id: (item as any).linked_procedure_id || undefined,
          linked_product_id: (item as any).linked_product_id || undefined,
          linked_treatment_id: (item as any).linked_treatment_id || undefined,
          based_on_standard_id: item.id,  // Critical: link to standard for proper shadowing
        });
        
        // Navigate to the new custom item
        setShowAddPrice(false);
        setNewPriceValue('');
        setSuccessMessage('Created practice override with new price');
        setTimeout(() => {
          navigate(`/settings/catalog/${newItem.id}/edit`);
        }, 500);
      } else {
        // For custom items, just add a new price
        await addNewPrice(user.practice_id, itemId, priceNum);
        const updated = await getPriceHistory(user.practice_id, itemId);
        setPriceHistory(updated);
        setFormData(prev => ({ ...prev, unit_price: priceNum }));
        setItem({ ...item, unit_price: priceNum });
        setShowAddPrice(false);
        setNewPriceValue('');
        setSuccessMessage('New price added successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add new price');
    } finally {
      setAddingPrice(false);
    }
  };

  const handleCopyPriceId = (priceId: string) => {
    navigator.clipboard.writeText(priceId);
    setShowPriceMenu(null);
  };

  const handleRevertToStandard = async () => {
    if (!user?.practice_id || !itemId || !item || !standardItem) return;
    
    setAddingPrice(true);
    setError(null);
    
    try {
      await addNewPrice(user.practice_id, itemId, standardItem.unit_price);
      const updated = await getPriceHistory(user.practice_id, itemId);
      setPriceHistory(updated);
      setFormData(prev => ({ ...prev, unit_price: standardItem.unit_price }));
      setItem({ ...item, unit_price: standardItem.unit_price });
      setSuccessMessage('Price updated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update price');
    } finally {
      setAddingPrice(false);
    }
  };

  const handleSetAsDefault = async (priceValue: number) => {
    if (!user?.practice_id || !itemId || !item) return;
    
    setAddingPrice(true);
    setError(null);
    setShowPriceMenu(null);
    
    try {
      await addNewPrice(user.practice_id, itemId, priceValue);
      const updated = await getPriceHistory(user.practice_id, itemId);
      setPriceHistory(updated);
      setFormData(prev => ({ ...prev, unit_price: priceValue }));
      setItem({ ...item, unit_price: priceValue });
      setSuccessMessage('Price updated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update price');
    } finally {
      setAddingPrice(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'Diagnostic': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'Treatment': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'Medication': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Product': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'Labs': return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300';
      case 'Immunizations': return 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !item) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Error Loading Item</h2>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Link 
              to="/settings/catalog"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Back to Catalog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!item) return null;

  // Build prices list - include current price from item if no history exists
  // Build prices list - include standard price reference for custom overrides
  const basePrices = priceHistory?.prices && priceHistory.prices.length > 0 
    ? priceHistory.prices 
    : item.unit_price > 0 
      ? [{ 
          id: 'initial', 
          unit_price: item.unit_price, 
          is_current: true, 
          effective_date: item.created_at, 
          created_at: item.created_at 
        }]
      : [];
  
  // Add standard price as reference if this is a custom override
  const displayPrices = standardItem 
    ? [
        ...basePrices,
        { 
          id: 'standard-reference', 
          unit_price: standardItem.unit_price, 
          is_current: false, 
          is_standard_reference: true,
          effective_date: standardItem.created_at, 
          created_at: standardItem.created_at 
        }
      ]
    : basePrices;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-3">
            <Link to="/settings" className="hover:text-gray-700 dark:hover:text-gray-200">Settings</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/settings/catalog" className="hover:text-gray-700 dark:hover:text-gray-200">Billing Catalog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 dark:text-white font-medium">{item.name}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{item.name}</h1>
            </div>
            
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                item.is_active 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                <div className={`w-2 h-2 rounded-full ${item.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                {item.is_active ? 'Active' : 'Inactive'}
              </div>
              
              <button
                onClick={handleSave}
                disabled={saving || item.is_standard}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium transition-colors"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {(error || successMessage) && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
              <button onClick={() => setError(null)} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {successMessage && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-800 dark:text-green-300 text-sm flex items-center gap-2">
              <Check className="w-4 h-4 flex-shrink-0" />
              {successMessage}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="space-y-6">
          {/* Pricing Section - Stripe Style */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-visible">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pricing</h2>
              <button
                onClick={() => setShowAddPrice(true)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={item.is_standard ? "Create practice override with custom price" : "Add new price"}
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            {/* Price Table - Stripe Style */}
            <div ref={menuRef}>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingPrices ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      </td>
                    </tr>
                  ) : displayPrices.length > 0 ? (
                    displayPrices.map((price) => (
                      <tr key={price.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 relative">
                        <td className="px-6 py-4">
                          <div className="text-blue-600 dark:text-blue-400 font-semibold">
                            {formatPrice(price.unit_price)} USD
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Per {item.measure_unit || 'unit'}</div>
                        </td>
                        <td className="px-6 py-4 text-gray-500 dark:text-gray-400">—</td>
                        <td className="px-6 py-4">
                          {price.is_current ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md border border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                              Current
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400">
                              Archived
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(price.created_at)}
                        </td>
                        <td className="px-6 py-4 text-right relative">
                          <button
                            onClick={() => setShowPriceMenu(showPriceMenu === price.id ? null : price.id)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <MoreHorizontal className="w-5 h-5" />
                          </button>
                          
                          {showPriceMenu === price.id && (
                            <div className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1 min-w-[200px] z-[9999]" style={{
                              top: 'auto',
                              right: '24px',
                              marginTop: '4px'
                            }}>
                              <button 
                                onClick={() => handleCopyPriceId(price.id)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                              >
                                <Copy className="w-4 h-4" />
                                Copy price ID
                              </button>
                              
                              <div className="border-t border-gray-100 dark:border-gray-700 my-1"></div>
                              
                              {!price.is_current && (
                                <button 
                                  onClick={() => {
                                    if ((price as any).is_standard_reference) {
                                      handleRevertToStandard();
                                    } else {
                                      handleSetAsDefault(price.unit_price);
                                    }
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  Set as default price
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        <p className="font-medium">No prices configured</p>
                        <p className="text-sm mt-1">Click + to add a price</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add Price Modal */}
          {showAddPrice && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" onClick={() => setShowAddPrice(false)}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {item.is_standard ? 'Create Practice Override' : 'Add a price'}
                  </h3>
                  <button 
                    onClick={() => setShowAddPrice(false)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="p-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {item.is_standard ? 'Your Practice Price' : 'Price'}
                  </label>
                  <div className="flex items-center gap-3 mb-4">
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={newPriceValue}
                      onChange={(e) => setNewPriceValue(e.target.value)}
                      className="flex-1 px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                      placeholder="0.00"
                      autoFocus
                    />
                    <span className="text-gray-500 dark:text-gray-400 font-medium">USD</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                    Per {item.measure_unit || 'unit'}
                  </p>
                  {item.is_standard && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                      Standard price: {formatPrice(item.unit_price)}
                    </p>
                  )}
                  {!item.is_standard && <div className="mb-4" />}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowAddPrice(false);
                        setNewPriceValue('');
                      }}
                      className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddNewPrice}
                      disabled={addingPrice || !newPriceValue}
                      className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {addingPrice ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        item.is_standard ? 'Create Override' : 'Add price'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Item Details Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Item Details</h2>
            </div>
            <div className="p-6 space-y-5">
              {/* Name - Always read-only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Item Name
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  readOnly
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isLinkedToExternal 
                    ? `Managed in ${linkedCatalogType === 'immunization' ? 'Immunization' : linkedCatalogType === 'diagnostic' ? 'Labs' : 'Medication'} Catalog`
                    : 'Name cannot be changed after creation'
                  }
                </p>
              </div>

              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Code</label>
                <input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  disabled={item.is_standard}
                  className={`w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                    item.is_standard ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : ''
                  }`}
                  placeholder="e.g., EXAM-W"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={item.is_standard}
                  rows={3}
                  className={`w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                    item.is_standard ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : ''
                  }`}
                  placeholder="Internal notes for staff"
                />
              </div>
            </div>
          </div>

          {/* Billing Settings Card */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Billing Settings</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Unit Cost */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Unit Cost (COGS)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unit_cost || ''}
                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value ? parseFloat(e.target.value) : undefined })}
                    disabled={item.is_standard}
                    className={`w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                      item.is_standard ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : ''
                    }`}
                    placeholder="Optional"
                  />
                </div>

                {/* Measure Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Measure Unit</label>
                  <input
                    type="text"
                    value={formData.measure_unit || ''}
                    onChange={(e) => setFormData({ ...formData, measure_unit: e.target.value })}
                    disabled={item.is_standard}
                    className={`w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                      item.is_standard ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : ''
                    }`}
                    placeholder="e.g., Tablet, Dose"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 pt-2">
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.taxable ?? true}
                    onChange={(e) => setFormData({ ...formData, taxable: e.target.checked })}
                    disabled={item.is_standard}
                    className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">Taxable</span>
                </label>
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.allow_price_override ?? false}
                    onChange={(e) => setFormData({ ...formData, allow_price_override: e.target.checked })}
                    disabled={item.is_standard}
                    className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">Allow price override at time of sale</span>
                </label>
              </div>

              {/* Margin display */}
              {formData.unit_cost && (formData.unit_price || item.unit_price) > 0 && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Profit Margin: </span>
                    {formatPrice((formData.unit_price || item.unit_price) - formData.unit_cost)} 
                    {' '}({(((formData.unit_price || item.unit_price) - formData.unit_cost) / (formData.unit_price || item.unit_price) * 100).toFixed(1)}%)
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Standard Item Notice */}
          {item.is_standard && (
            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">Standard Item</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                This is a system-wide standard item. Create a practice override to customize pricing for your practice.
              </p>
              <Link 
                to={`/settings/catalog/new?override=${item.id}`}
                className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
              >
                Create Override
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CatalogItemEditPage;
